/**
 * Model type constants for all objects in the Benchling data model.
 *
 * Newer models are expected to use DomainGraphType as the source of truth for type identity since it is
 * guaranteed to stay in sync with types in the Domain Graph, but this file is still used in many places and
 * newer types will still add to ModelTypeConstants for backwards compatibility.
 */

import _ from 'underscore';

import {NucleotideType} from 'biojs/BioConstants';

// NOTE: This is used by search code generation to identify the constant to use in listing renderers and
// the like. If you update the format of this, get_search_model_type_constants_for_types in
// scripts/search/code_generation/client_helpers.py will need to be updated to extract the constant
// as required.
//
// _DO_NOT_USE_OLIGO_CONJUGATE and _DO_NOT_USE_OLIGO_DUPLEX are NOT to be used for oligo conjugates and oligo
// duplexes on the frontend. Instead use BASIC_FOLDER_ITEM - this is the backing entity for complex polymers
// and is already in use for the complex polymers frontend.
// These constants are added as placeholders to satisfy a domain graph requirement that there is a
// 1:1 mapping between domain models and model type constants.
// Ideally, this will be improved for future exclusive SSIs.

// NOTE: Do not add comments in the body of ModelTypeConstants because they will get overridden
// by the bootstrap script
enum ModelTypeConstants {
  _DO_NOT_USE_OLIGO_CONJUGATE = 'oligo_conjugate',
  _DO_NOT_USE_OLIGO_DUPLEX = 'oligo_duplex',
  ANALYSIS = 'analysis',
  ANALYSIS_TEMPLATE = 'pipeline_template',
  ANALYSIS_TEMPLATE_DATA_FRAME_VARIABLE = 'analysis_template_data_frame_variable',
  ANALYSIS_TEMPLATE_VARIABLE = 'analysis_template_variable',
  ANALYTICS_DASHBOARD = 'analytics_dashboard',
  ANALYTICS_QUERY = 'analytics_query',
  ANNOTATION = 'annotation',
  ASSAY_PROCEDURE = 'assay_procedure',
  ASSAY_PROCESS = 'assay_process',
  ASSAY_PROCESS_OUTPUT = 'assay_process_output',
  ASSAY_RESULT = 'assay_result',
  ASSAY_RUN = 'assay_run',
  ASSAY_RUN_TASK = 'assay_run_task',
  ASSAY_SCHEMA = 'assay_schema',
  ASSAY_STEP_TASK = 'assay_step_task',
  ASSAY_UNIT_OPERATION_TASK = 'assay_unit_operation_task',
  AUTOMATION_INPUT_TRANSFORM_FILE = 'automation_input_transform_file',
  AUTOMATION_OUTPUT_FILE = 'automation_output_file',
  AUTOMATION_OUTPUT_TRANSFORM_FILE = 'automation_output_transform_file',
  BASIC_FOLDER_ITEM = 'basic_folder_item',
  BATCH = 'batch',
  BATCH_SCHEMA = 'batch_schema',
  BENCHLING_APP = 'benchling_app',
  BENCHLING_OBJECT_SCHEMA_V2 = 'benchling_object_schema',
  BIOENTITY_MIXIN = 'BIOENTITY_MIXIN',
  BLOB = 'result_blob',
  BLOCK_VISUALIZATION = 'block_visualization',
  BOX = 'box',
  BOX_SCHEMA = 'box_schema',
  BULK_ASSEMBLY = 'bulk_assembly',
  BUSINESS_RULE = 'business_rule',
  CHART = 'chart',
  COLLABORATION = 'collaboration',
  CONFIG_IMPORT_HISTORY_ENTRY = 'config_import_history_entry',
  CONNECTION = 'connection',
  CONNECTION_SCHEMA = 'connection_schema',
  CONTAINER = 'container',
  CONTAINER_SCHEMA = 'container_schema',
  DATA_FRAME = 'data_frame',
  DATA_TABLE = 'data_table',
  DATA_TABLE_CONFIG = 'data_table_config',
  DATASET = 'dataset',
  DNA_OLIGO = 'oligo',
  DOCUMENT = 'document',
  DOCUMENT_VERSION = 'document_version',
  ENTITY = 'entity',
  ENTRY = 'entry',
  ENTRY_PROTOCOL = 'entry_protocol',
  ENZYME_LIST = 'enzyme_list',
  EXTERNAL_FILE = 'external_file',
  FEATURE_LIBRARY = 'feature_library',
  FILE = 'pipeline_file',
  FIXED_PLATE = 'fixed_plate',
  FOLDER = 'folder',
  FORM_DEFINITION = 'form_definition',
  GRID = 'grid',
  INGREDIENT = 'ingredient',
  INSTITUTION = 'institution',
  JELLYFISH = 'jellyfish',
  JELLYFISH_SCHEMA = 'jellyfish_schema',
  LABEL_TEMPLATE = 'label_template',
  LADDER = 'ladder',
  LOCATION = 'location',
  LOCATION_SCHEMA = 'location_schema',
  LOOKUP_TABLE = 'lookup_table',
  MATRIX_PLATE = 'matrix_plate',
  MATRIX_PLATE_SCHEMA = 'matrix_plate_schema',
  MIXTURE = 'mixture',
  MIXTURE_INSTRUCTIONS = 'mixture_instructions',
  MOLECULE = 'molecule',
  MONOMER = 'monomer',
  NOTE_FILL_IN_TEXT_BOX = 'note_fill_in_text_box',
  NOTE_LINKED_APP_CANVAS = 'note_linked_app_canvas',
  NOTE_LINKED_ASSAY_DATA = 'note_linked_assay_data',
  NOTE_LINKED_CHART = 'note_linked_chart',
  NOTE_LINKED_DATA = 'note_linked_data',
  NOTE_LINKED_FILE = 'note_linked_file',
  NOTE_LINKED_GROUP = 'note_linked_group',
  NOTE_LINKED_OBJECT = 'note_linked_object',
  NOTE_LINKED_PLATE_DESIGN = 'note_linked_plate_design',
  NOTE_LINKED_PLATE_RECORD = 'note_linked_plate_transfer_record',
  ORGANIZATION = 'organization',
  PLATE = 'plate',
  PLATE_SCHEMA = 'plate_schema',
  PRIMER_PAIR = 'primer_pair',
  PRINTER = 'printer',
  PROCEDURE = 'procedure',
  PROCEDURE_FLOWCHART = 'procedure_flowchart',
  PROCEDURE_FLOWCHART_VERSION = 'procedure_flowchart_version',
  PROCEDURE_METHOD_DEFINITION_VERSION = 'procedure_method_definition_version',
  PROCEDURE_METHOD_EXECUTION_INSTANCE = 'procedure_method_execution_instance',
  PROCEDURE_PARAMETER = 'procedure_parameter',
  PROCEDURE_PARAMETER_CONFIRMATION_VALUE = 'procedure_parameter_confirmation_value',
  PROCEDURE_PARAMETER_MEASURED_VALUE = 'procedure_parameter_measured_value',
  PROCEDURE_PARAMETER_PLANNED_VALUE = 'procedure_parameter_planned_value',
  PROCEDURE_PARAMETER_VERSION = 'procedure_parameter_version',
  PROCEDURE_RUN = 'procedure_run',
  PROCEDURE_RUN_CONDITION = 'procedure_run_condition',
  PROCEDURE_RUN_CONDITION_REPLICATE = 'procedure_run_condition_replicate',
  PROCEDURE_STEP_DEFINITION_VERSION = 'procedure_step_definition_version',
  PROCEDURE_STEP_EXECUTION_INSTANCE = 'procedure_step_execution_instance',
  PROCESS = 'process',
  PROCESS_FLOWCHART = 'process_flowchart',
  PROCESS_FLOWCHART_CONFIG_VERSION = 'process_flowchart_config_version',
  PROCESS_OUTPUT = 'process_output',
  PROCESS_OUTPUT_SCHEMA = 'process_output_schema',
  PROCESS_SCHEMA = 'process_schema',
  PROCESS_TASK = 'process_task',
  PROCESS_TASK_SCHEMA = 'process_task_schema',
  PROJECT = 'project',
  PROTEIN = 'protein',
  PROTEIN_ALIGNMENT = 'protein_alignment',
  PROTOCOL = 'protocol',
  PUBLISHING_RECORD = 'publishing_record',
  RECIPE_PROCEDURE = 'recipe_procedure',
  RECIPE_PROCESS = 'recipe_process',
  RECIPE_PROCESS_OUTPUT = 'recipe_process_output',
  RECIPE_RUN_TASK = 'recipe_run_task',
  RECIPE_STEP_TASK = 'recipe_step_task',
  RECIPE_UNIT_OPERATION_TASK = 'recipe_unit_operation_task',
  REGISTRATION_TABLE = 'registration_table',
  REGISTRATION_TABLE_CONFIG = 'registration_table_config',
  REGISTRY = 'registry',
  REQUEST = 'assay_request',
  REQUEST_SCHEMA = 'request_schema',
  REQUEST_TASK_SCHEMA = 'request_task_schema',
  REQUEST_V2_DEFINITION = 'request_v2_definition',
  REQUEST_V2_SUBMISSION = 'request_v2_submission',
  RESULT_SCHEMA = 'result_schema',
  REVIEW = 'review',
  REVIEW_LIFECYCLE = 'review_lifecycle',
  RNA_OLIGO = 'rna_oligo',
  RNA_SEQUENCE = 'rna_sequence',
  RUN_SCHEMA = 'run_schema',
  SAMPLE = 'sample',
  SAMPLE_DEFINITION = 'sample_definition',
  SAMPLE_TYPE = 'sample_type',
  SAVED_SEARCH = 'saved_search',
  SCHEMA_FIELD = 'tag_schema_field',
  SCHEMA_FIELD_SELECTOR = 'schema_field_selector',
  SCHEMA_FIELD_SELECTOR_OPTION = 'schema_field_selector_option',
  SCHEMA_INTERFACE = 'schema_interface',
  SERVICE_ACCOUNT = 'service_account',
  SEQUENCE = 'sequence',
  SEQUENCE_ANALYSIS = 'sequence_analysis',
  SEQUENCE_PRIMER = 'sequence_primer',
  SIMPLE_TABLE_DOC = 'SIMPLE_TABLE_DOC',
  SOURCE = 'source',
  SQL_DASHBOARD = 'sql_dashboard',
  SQL_DASHBOARD_BLOCK = 'sql_dashboard_block',
  SQL_DASHBOARD_LINK = 'sql_dashboard_link',
  STAGE_CONFIGURATION = 'stage_configuration',
  STAGE_ENTRY = 'stage_entry',
  STAR = 'star',
  STATIC_SCHEMA_INTERFACE = 'STATIC_SCHEMA_INTERFACE',
  STORABLE = 'STORABLE',
  STRUCTURED_TABLE = 'structured_table',
  STUDY = 'study',
  STUDY_SAMPLE_TEST_PLAN = 'study_sample_test_plan',
  STUDY_DESIGN = 'study_design',
  STUDY_SCHEMA = 'study_schema',
  SUBTEMPLATE = 'subtemplate',
  TAG_SCHEMA = 'tag_schema',
  TEAM = 'team',
  TEMPLATE = 'template',
  TEMPLATE_COLLECTION = 'template_collection',
  TENANT_CONFIG = 'tenant_config',
  TENTACLE = 'tentacle_table',
  TEST_DEFINITION = 'test_definition',
  TEST_ORDER = 'test_order',
  TFI_ACCOUNT = 'tfi_account',
  TRANSFER_TABLE = 'transfer_table',
  TRANSFER_TABLE_CONFIG = 'transfer_table_config',
  TRANSFER_TABLE_PLACEHOLDER = 'transfer_table_placeholder',
  TREATMENT_GROUP = 'treatment_group',
  TRIAL_SAMPLE_TESTING_STUDY_DESIGN = 'trial_sample_testing_study_design',
  UNIT = 'unit',
  UNIT_TYPE = 'quantity',
  USER = 'user',
  VALIDATION_RULE = 'validation_rule',
  WELL_PLATE_SCHEMA = 'well_plate_schema',
  WORKFLOW = 'workflow',
  WORKFLOW_TEMPLATE = 'workflow_template',
  WORKLIST = 'worklist',
  WORKSHEET = 'worksheet',
}

export type SchemaType = ModelTypeConstants.BATCH_SCHEMA | ModelTypeConstants.TAG_SCHEMA;

export type EntryType =
  | ModelTypeConstants.ENTRY
  | ModelTypeConstants.STAGE_CONFIGURATION
  | ModelTypeConstants.STAGE_ENTRY
  | ModelTypeConstants.SUBTEMPLATE
  | ModelTypeConstants.TEMPLATE
  | ModelTypeConstants.DOCUMENT;

export type ProcessType =
  | ModelTypeConstants.PROCESS
  | ModelTypeConstants.RECIPE_PROCESS
  | ModelTypeConstants.ASSAY_PROCESS;

export const PROCESS_TYPES: ReadonlyArray<ModelTypeConstants> = [
  ModelTypeConstants.PROCESS,
  ModelTypeConstants.RECIPE_PROCESS,
  ModelTypeConstants.ASSAY_PROCESS,
] as const;

export type FolderItemType =
  | ProcessType
  | ModelTypeConstants.BASIC_FOLDER_ITEM
  | ModelTypeConstants.BULK_ASSEMBLY
  | ModelTypeConstants.DATASET
  | ModelTypeConstants.ENTRY
  | ModelTypeConstants.FILE
  | ModelTypeConstants.MIXTURE
  | ModelTypeConstants.MOLECULE
  | ModelTypeConstants.PROTEIN
  | ModelTypeConstants.PROTOCOL
  | ModelTypeConstants.SEQUENCE
  | ModelTypeConstants.STUDY;

export const FOLDER_ITEM_TYPES: ReadonlyArray<ModelTypeConstants> = [
  ...PROCESS_TYPES,
  ModelTypeConstants.BASIC_FOLDER_ITEM,
  ModelTypeConstants.BULK_ASSEMBLY,
  ModelTypeConstants.DATASET,
  ModelTypeConstants.ENTRY,
  ModelTypeConstants.FILE,
  ModelTypeConstants.MIXTURE,
  ModelTypeConstants.MOLECULE,
  ModelTypeConstants.PROTEIN,
  ModelTypeConstants.PROTOCOL,
  ModelTypeConstants.REQUEST_V2_SUBMISSION,
  ModelTypeConstants.SEQUENCE,
  ModelTypeConstants.STUDY,
] as const;

// Keep in-sync with FolderItemMixin.Context.SHARE_LINK
// eslint-disable-next-line no-restricted-syntax
export const SHARE_LINK_FOLDER_ITEMS: Array<ModelTypeConstants> = _.without(
  FOLDER_ITEM_TYPES,
  ModelTypeConstants.DATASET,
  ...PROCESS_TYPES,
  ModelTypeConstants.MIXTURE,
  ModelTypeConstants.BULK_ASSEMBLY,
  ModelTypeConstants.STUDY,
  ModelTypeConstants.REQUEST_V2_SUBMISSION
);

// Keep in-sync with benchling/exports/human_exports/app/
// eslint-disable-next-line no-restricted-syntax
export const EXPORTABLE_FOLDER_ITEMS: Array<ModelTypeConstants> = _.without(
  FOLDER_ITEM_TYPES,
  ModelTypeConstants.DATASET,
  ...PROCESS_TYPES,
  ModelTypeConstants.BULK_ASSEMBLY,
  ModelTypeConstants.STUDY,
  ModelTypeConstants.REQUEST_V2_SUBMISSION
);

// Keep in-sync with FolderItemMixin.clone implementation
// eslint-disable-next-line no-restricted-syntax
export const COPYABLE_FOLDER_ITEMS: Array<ModelTypeConstants> = _.without(
  FOLDER_ITEM_TYPES,
  ModelTypeConstants.DATASET,
  ...PROCESS_TYPES,
  ModelTypeConstants.BULK_ASSEMBLY,
  ModelTypeConstants.STUDY,
  ModelTypeConstants.REQUEST_V2_SUBMISSION
);

// eslint-disable-next-line no-restricted-syntax
export const QUICK_EDIT_FOLDER_ITEMS: Array<ModelTypeConstants> = _.without(
  FOLDER_ITEM_TYPES,
  ModelTypeConstants.DATASET,
  ModelTypeConstants.PROTOCOL,
  ...PROCESS_TYPES,
  ModelTypeConstants.MOLECULE,
  ModelTypeConstants.BULK_ASSEMBLY,
  ModelTypeConstants.STUDY,
  ModelTypeConstants.REQUEST_V2_SUBMISSION
);

// eslint-disable-next-line no-restricted-syntax
export const CUSTOM_TAGGABLE_FOLDER_ITEMS: Array<ModelTypeConstants> = _.without(
  FOLDER_ITEM_TYPES,
  ...PROCESS_TYPES,
  ModelTypeConstants.BULK_ASSEMBLY,
  ModelTypeConstants.STUDY,
  ModelTypeConstants.REQUEST_V2_SUBMISSION
);

/** List of items that can show user presence when viewed in a buffer. */
export const PRESENCE_ENABLED_FOLDER_ITEMS: ReadonlyArray<ModelTypeConstants> = [
  ModelTypeConstants.ENTRY,
] as const;

/**
 * TypeScript type representing the ModelTypeConstants values relevant in supporting the oligo/long sequence
 * split. Variables typed with SequenceSubtype are expected to account for the DNA/oligo split explicitly as
 * part of the static type checking process by selecting the most appropriate ModelTypeConstants value.
 *
 * SEQUENCE here always means long sequence as opposed to oligos, not the generic term for either.
 */
export type SequenceSubtype =
  | ModelTypeConstants.SEQUENCE
  | ModelTypeConstants.DNA_OLIGO
  | ModelTypeConstants.RNA_OLIGO
  | ModelTypeConstants.RNA_SEQUENCE;

export const SEQUENCE_SUBTYPES: ReadonlyArray<ModelTypeConstants> = [
  ModelTypeConstants.SEQUENCE,
  ModelTypeConstants.DNA_OLIGO,
  ModelTypeConstants.RNA_OLIGO,
  ModelTypeConstants.RNA_SEQUENCE,
] as const;

// The bioentity search endpoint parses "oligo" to mean all oligos, so we use "dna_oligo" instead
// as the filter type for only DNA oligos.
export const DNA_OLIGO_SEARCH_TYPE = 'dna_oligo';

export const RNA_OLIGO_SEARCH_TYPE = 'rna_oligo';

// These special filters allow searching by sequence subtypes.
export const ALL_DNA_SEARCH_TYPE = 'all_dna';
export const ALL_RNA_SEARCH_TYPE = 'all_rna';

type NonSequenceBioentityType =
  | ModelTypeConstants.BASIC_FOLDER_ITEM
  | ModelTypeConstants.PROTEIN
  | ModelTypeConstants.MIXTURE
  | ModelTypeConstants.MOLECULE;

export type BioentityType = SequenceSubtype | NonSequenceBioentityType;

export const LONG_SEQUENCE_NUCLEOTIDE_TYPE_TO_SEQUENCE_SUBTYPE: Record<NucleotideType, SequenceSubtype> = {
  [NucleotideType.DNA]: ModelTypeConstants.SEQUENCE,
  [NucleotideType.RNA]: ModelTypeConstants.RNA_SEQUENCE,
};

export const OLIGO_NUCLEOTIDE_TYPE_TO_SEQUENCE_SUBTYPE: Record<NucleotideType, SequenceSubtype> = {
  [NucleotideType.DNA]: ModelTypeConstants.DNA_OLIGO,
  [NucleotideType.RNA]: ModelTypeConstants.RNA_OLIGO,
};

export const NOTEBOOK_ITEMS: ReadonlyArray<ModelTypeConstants> = [
  ModelTypeConstants.ENTRY,
  ModelTypeConstants.PROTOCOL,
] as const;

export type StorableItemType =
  | ModelTypeConstants.BOX
  | ModelTypeConstants.CONTAINER
  | ModelTypeConstants.LOCATION
  | ModelTypeConstants.PLATE
  | ModelTypeConstants.MATRIX_PLATE
  | ModelTypeConstants.FIXED_PLATE;

export const STORABLE_ITEM_TYPES: ReadonlyArray<ModelTypeConstants> = [
  ModelTypeConstants.BOX,
  ModelTypeConstants.CONTAINER,
  ModelTypeConstants.LOCATION,
  ModelTypeConstants.PLATE,
] as const;

export const PARENT_STORABLE_ITEMS: ReadonlyArray<ModelTypeConstants> = [
  ModelTypeConstants.BOX,
  ModelTypeConstants.LOCATION,
  ModelTypeConstants.MATRIX_PLATE,
] as const;

export const PREVIEWABLE_NOTE_LINKED_OBJECT_TYPES: ReadonlyArray<ModelTypeConstants> = [
  ModelTypeConstants.NOTE_LINKED_FILE,
] as const;

export const POLYMERS: ReadonlyArray<ModelTypeConstants> = [
  ModelTypeConstants.SEQUENCE,
  ModelTypeConstants.PROTEIN,
  ModelTypeConstants.DNA_OLIGO,
] as const;

export const STORABLE_TO_SCHEMA: Partial<Record<StorableItemType, ModelTypeConstants>> = {
  [ModelTypeConstants.BOX]: ModelTypeConstants.BOX_SCHEMA,
  [ModelTypeConstants.CONTAINER]: ModelTypeConstants.CONTAINER_SCHEMA,
  [ModelTypeConstants.LOCATION]: ModelTypeConstants.LOCATION_SCHEMA,
  [ModelTypeConstants.PLATE]: ModelTypeConstants.PLATE_SCHEMA,
};

export const SCHEMA_TO_STORABLE: Partial<Record<ModelTypeConstants, StorableItemType>> = {
  [ModelTypeConstants.BOX_SCHEMA]: ModelTypeConstants.BOX,
  [ModelTypeConstants.CONTAINER_SCHEMA]: ModelTypeConstants.CONTAINER,
  [ModelTypeConstants.LOCATION_SCHEMA]: ModelTypeConstants.LOCATION,
  [ModelTypeConstants.PLATE_SCHEMA]: ModelTypeConstants.PLATE,
};

export type NoteLinkedDataType = ModelTypeConstants.NOTE_LINKED_PLATE_DESIGN;

export default ModelTypeConstants;
